import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import styled, { css } from 'styled-components';
import { i as isMobile } from '../../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { u as useTablistContext } from '../TablistContext-Bpw2P1bl.js';
import { f as focusOutline } from '../../theme/style-mixins/FocusIndicator/focusOutline-oDdaDZ4N.js';
import { K as KEYBOARD_KEYNAMES } from '../../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';

const focusedTabStyle = /*#__PURE__*/css([":focus-visible{", " box-shadow:none;", "}"], focusOutline, isMobile(css(["outline-offset:-2px;"])));
const activeTabStyle = theme => `
  color: ${theme.tabbedNavigation.text.color.active};
  font-weight: ${theme.font.weight.bold};
`;
const baseTabStyle = theme => css(["font:", ";padding-block-start:", ";padding-block-end:", ";", ""], theme.font.text.medium.default, theme.spacing.xxsmall.default, theme.spacing.xsmall.default, isMobile(css(["font:", ";padding-block-start:", ";padding-block-end:", ";"], theme.font.text.medium.mobile, theme.spacing.xxsmall.mobile, theme.spacing.xsmall.mobile)));
const smallTabStyle = theme => css(["font:", ";padding-block-start:", ";padding-block-end:", ";", ""], theme.font.text.base.default, theme.spacing.xxsmall.default, theme.spacing.xxsmall.default, isMobile(css(["font:", ";padding-block-start:", ";padding-block-end:", ";"], theme.font.text.base.mobile, theme.spacing.xxsmall.mobile, theme.spacing.xxsmall.mobile)));
const tabStyle = (label, theme) => css(["display:inline-flex;align-items:center;background:none;border-top:none;border-left:none;border-right:none;border-bottom:3px solid transparent;margin:0;cursor:pointer;height:auto;color:", ";padding-inline:0;flex-direction:column;justify-content:space-between;:hover{border-bottom:3px solid transparent;color:", ";text-decoration:none;}::after{content:\"", "\";content:\"", "\" / \"\";height:0;visibility:hidden;overflow:hidden;user-select:none;pointer-events:none;font-weight:bold;@media speech{display:none;}}"], theme.text.color.secondary, theme.tabbedNavigation.text.color.hover, label, label);
const ButtonTab = /*#__PURE__*/styled.button.withConfig({
  componentId: "sc-storm-ui-40019350__sc-1kglx9i-0"
})(["", ";", " ", ";", ""], ({
  $label,
  theme
}) => tabStyle($label, theme), ({
  theme,
  $size
}) => $size === 'small' ? smallTabStyle(theme) : baseTabStyle(theme), ({
  theme,
  $active
}) => $active && activeTabStyle(theme), focusedTabStyle);
const LinkTab = /*#__PURE__*/styled.a.withConfig({
  componentId: "sc-storm-ui-40019350__sc-1kglx9i-1"
})(["border-bottom:", " solid transparent;", " ", " ", ";:active,:visited{color:", ";}:link{color:", ";text-decoration:none;}", ""], ({
  theme
}) => theme.tabbedNavigation.highlight.height, ({
  $label,
  theme
}) => tabStyle($label, theme), ({
  theme,
  $size
}) => $size === 'small' ? smallTabStyle(theme) : baseTabStyle(theme), ({
  theme,
  $active
}) => $active && activeTabStyle(theme), ({
  theme
}) => theme.text.color.secondary, ({
  theme
}) => theme.text.color.secondary, focusedTabStyle);
const TextTab = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-storm-ui-40019350__sc-1kglx9i-2"
})(["", " :last-of-type{margin-inline-end:0;}color:", ";", ""], ({
  theme,
  $size
}) => $size === 'small' ? smallTabStyle(theme) : baseTabStyle(theme), ({
  theme
}) => theme.text.color.secondary, isMobile(css(["padding-block-start:", ";padding-block-end:", ";white-space:nowrap;"], ({
  theme
}) => theme.spacing.xxsmall.mobile, ({
  theme
}) => theme.spacing.xsmall.mobile)));
const handleKeyDown = event => {
  // By default, buttons handle the Enter key on KeyDown. We prevent this because we want
  // to listen to KeyUp to prevent repeat triggers.
  if (event.key === KEYBOARD_KEYNAMES.ENTER) {
    event.preventDefault();
  }
};
const handleKeyUp = event => {
  const {
    key
  } = event;
  //  Dispatch a fake mouse click to the Tab when a ENTER or SPACE key are released
  if (key === KEYBOARD_KEYNAMES.ENTER || key === KEYBOARD_KEYNAMES.SPACE) {
    const {
      currentTarget
    } = event;
    const mouseEvent = new MouseEvent('click', {
      view: currentTarget.ownerDocument.defaultView,
      bubbles: true,
      cancelable: true
    });
    currentTarget.dispatchEvent(mouseEvent);
    event.preventDefault();
  }
};
const Tab = ({
  active,
  dataTaktAttributes,
  href,
  label,
  onClick,
  size = 'base',
  ...rest
}) => {
  const {
    isTablist
  } = useTablistContext();
  if (href) {
    return /*#__PURE__*/React__default.createElement("span", _extends({}, isTablist ? {
      role: 'tab'
    } : {}, {
      "aria-selected": active
    }), /*#__PURE__*/React__default.createElement(LinkTab, _extends({}, dataTaktAttributes, {
      // {...rest}
      $label: typeof label === 'string' ? label : '',
      $size: size,
      $active: active,
      onClick: onClick,
      href: href,
      role: "link"
    }, isTablist ? {
      tabIndex: active ? 0 : -1
    } : {}), label));
  }
  if (onClick) {
    return /*#__PURE__*/React__default.createElement(ButtonTab, _extends({}, dataTaktAttributes, {
      type: "button"
    }, rest, {
      $label: typeof label === 'string' ? label : '',
      $size: size,
      $active: active,
      "aria-selected": active,
      onClick: onClick
    }, isTablist ? {
      tabIndex: active ? 0 : -1,
      role: 'tab',
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp
    } : {}), label);
  }
  return /*#__PURE__*/React__default.createElement(TextTab, _extends({}, rest, {
    $size: size
  }, isTablist ? {
    tabIndex: -1,
    role: 'tab'
  } : {}), label);
};

export { Tab as T };
//# sourceMappingURL=Tab--W-K_Mkk.js.map
